import React, { useMemo, useState } from "react";
import {
  Box,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  SimpleGrid,
  Grid,
  Progress,
  Paper,
} from "@mantine/core";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { motion } from "framer-motion";
import {
  IconMapPin,
  IconUsers,
  IconMap2,
  IconTrophy,
  IconBuildingSkyscraper,
  IconSparkles,
  IconArrowUpRight,
} from "@tabler/icons-react";
import INDIA_TOPO from "./indiaStatesTopo.json";

// ==========================================
// GEOGRAPHIC REACH — INDIA (MOCK DATA)
// Sample state/city user distribution for the investor demo. Replace with a
// live geo-aggregation feed. State names below MUST match the `st_nm` property
// in indiaStatesTopo.json exactly, or a state renders as "no data".
// ==========================================

// Mock registered-user counts per state / UT. Tuned to sum to the platform's
// real current total (19,774) with a realistic metro-skewed distribution.
const STATE_USERS = {
  Karnataka: 3423,
  Maharashtra: 2780,
  Delhi: 2240,
  "Tamil Nadu": 1560,
  "Uttar Pradesh": 1380,
  Telangana: 1270,
  "West Bengal": 980,
  Gujarat: 820,
  Kerala: 760,
  "Andhra Pradesh": 610,
  Rajasthan: 540,
  Haryana: 520,
  "Madhya Pradesh": 430,
  Punjab: 360,
  Bihar: 300,
  Odisha: 240,
  Assam: 210,
  Chhattisgarh: 160,
  Jharkhand: 150,
  Uttarakhand: 150,
  Goa: 140,
  Chandigarh: 130,
  "Himachal Pradesh": 110,
  "Jammu and Kashmir": 95,
  Puducherry: 80,
  Manipur: 60,
  Tripura: 55,
  Meghalaya: 45,
  Nagaland: 35,
  Mizoram: 30,
  Sikkim: 28,
  "Arunachal Pradesh": 25,
  "Dadra and Nagar Haveli and Daman and Diu": 22,
  "Andaman and Nicobar Islands": 18,
  Ladakh: 12,
  Lakshadweep: 6,
};

// Zonal Council grouping — drives the donut. Direct-labeled, so the WARN-level
// contrast of the lighter hues is relieved.
const REGION_OF = {
  // North
  Delhi: "North", "Uttar Pradesh": "North", Rajasthan: "North", Haryana: "North",
  Punjab: "North", "Himachal Pradesh": "North", "Jammu and Kashmir": "North",
  Uttarakhand: "North", Chandigarh: "North", Ladakh: "North",
  // South
  Karnataka: "South", "Tamil Nadu": "South", Telangana: "South",
  "Andhra Pradesh": "South", Kerala: "South", Puducherry: "South",
  Lakshadweep: "South", "Andaman and Nicobar Islands": "South",
  // West
  Maharashtra: "West", Gujarat: "West", Goa: "West",
  "Dadra and Nagar Haveli and Daman and Diu": "West",
  // East
  "West Bengal": "East", Bihar: "East", Odisha: "East", Jharkhand: "East",
  // Central
  "Madhya Pradesh": "Central", Chhattisgarh: "Central",
  // Northeast
  Assam: "Northeast", Meghalaya: "Northeast", Manipur: "Northeast",
  Tripura: "Northeast", Nagaland: "Northeast", "Arunachal Pradesh": "Northeast",
  Mizoram: "Northeast", Sikkim: "Northeast",
};

const REGION_COLORS = {
  North: "#3969E7",
  South: "#07B9CE",
  West: "#7D2AE7",
  East: "#F59E0B",
  Central: "#10B981",
  Northeast: "#EC4899",
};
const REGION_ORDER = ["North", "South", "West", "East", "Central", "Northeast"];

// Top metros — [lng, lat] feed react-simple-maps markers directly (same
// projection as the choropleth, so they self-align). `label` = draw on map.
const CITIES = [
  { name: "Bengaluru", coordinates: [77.5946, 12.9716], users: 2420, label: "left" },
  { name: "Delhi NCR", coordinates: [77.209, 28.6139], users: 2180, label: "right" },
  { name: "Mumbai", coordinates: [72.8777, 19.076], users: 2000, label: "left" },
  { name: "Hyderabad", coordinates: [78.4867, 17.385], users: 1180 },
  { name: "Chennai", coordinates: [80.2707, 13.0827], users: 1120 },
  { name: "Kolkata", coordinates: [88.3639, 22.5726], users: 820, label: "left" },
  { name: "Pune", coordinates: [73.8567, 18.5204], users: 640 },
  { name: "Ahmedabad", coordinates: [72.5714, 23.0225], users: 590 },
  { name: "Kochi", coordinates: [76.2673, 9.9312], users: 430 },
  { name: "Jaipur", coordinates: [75.7873, 26.9124], users: 410 },
];

// ---- Sequential purple ramp (light -> dark), monotonic in lightness. Matches
// the existing spend/heatmap purple language elsewhere in the dashboard. ----
const RAMP = ["#F3EEFF", "#D9C9FB", "#B79CF3", "#8B5CF6", "#6D28D9", "#4C1D95"];
const NO_DATA = "#F7F5FC";

const hexToRgb = (h) => [
  parseInt(h.slice(1, 3), 16),
  parseInt(h.slice(3, 5), 16),
  parseInt(h.slice(5, 7), 16),
];
const rgbToHex = (r, g, b) =>
  "#" + [r, g, b].map((v) => Math.round(v).toString(16).padStart(2, "0")).join("");
const lerpHex = (a, b, t) => {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  return rgbToHex(
    ca[0] + (cb[0] - ca[0]) * t,
    ca[1] + (cb[1] - ca[1]) * t,
    ca[2] + (cb[2] - ca[2]) * t,
  );
};

// Share of users outside the four biggest metro states — the "we're not just a
// metro app" story for investors.
const METRO_STATES = new Set(["Maharashtra", "Karnataka", "Delhi", "Tamil Nadu"]);

// Fill for a state: sqrt scale so mid-tier states still read as colored rather
// than washing out under the metro peaks.
const colorForState = (users, maxState) => {
  if (!users) return NO_DATA;
  const t = Math.sqrt(users / maxState);
  const seg = t * (RAMP.length - 1);
  const i = Math.min(Math.floor(seg), RAMP.length - 2);
  return lerpHex(RAMP[i], RAMP[i + 1], seg - i);
};

const cityRadius = (users) => 3 + Math.sqrt(users) / 5.5;

const fmt = (n) => n.toLocaleString("en-IN");

// ---- Small presentational pieces ----

const GeoKpi = ({ icon, color, label, value, sub }) => (
  <Card withBorder radius="md" p="lg">
    <Group justify="space-between" mb="sm">
      <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
        {label}
      </Text>
      <ThemeIcon color={color} variant="light" size="sm" radius="xl">
        {icon}
      </ThemeIcon>
    </Group>
    <Text fw={800} size="1.7rem" lh={1.1}>
      {value}
    </Text>
    {sub && (
      <Text size="xs" c="dimmed" mt={4}>
        {sub}
      </Text>
    )}
  </Card>
);

// Lightweight donut drawn as SVG arcs (no extra chart lib) for the zonal split.
const ZonalDonut = ({ data, total }) => {
  const size = 180;
  const r = 70;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
      aria-label="Users by zone">
      <g transform={`rotate(-90 ${cx} ${cy})`}>
        {data.map((d) => {
          const frac = d.value / total;
          const dash = frac * circ;
          const seg = (
            <circle
              key={d.region}
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              stroke={d.color}
              strokeWidth={22}
              strokeDasharray={`${dash} ${circ - dash}`}
              strokeDashoffset={-offset}
            />
          );
          offset += dash;
          return seg;
        })}
      </g>
      <text x={cx} y={cy - 6} textAnchor="middle" fontSize={22} fontWeight={800}
        fill="#1f2937">
        {(total / 1000).toFixed(0)}K
      </text>
      <text x={cx} y={cy + 14} textAnchor="middle" fontSize={11} fill="#6b7280">
        total users
      </text>
    </svg>
  );
};

// ==========================================
// MAIN SECTION
// ==========================================
export default function IndiaReachSection({ geography = null }) {
  const [hovered, setHovered] = useState(null); // { name, users }
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const isLive = !!geography?.states?.length;

  // Real classified counts if we have them, else fall back to the demo set.
  const STATE_USERS_LIVE = useMemo(() => {
    if (!isLive) return STATE_USERS;
    return Object.fromEntries(geography.states.map((s) => [s.name, s.users]));
  }, [geography, isLive]);

  const STATE_ENTRIES = useMemo(
    () => Object.entries(STATE_USERS_LIVE).sort((a, b) => b[1] - a[1]),
    [STATE_USERS_LIVE],
  );
  const TOTAL_USERS = useMemo(
    () => STATE_ENTRIES.reduce((s, [, v]) => s + v, 0),
    [STATE_ENTRIES],
  );
  const MAX_STATE = STATE_ENTRIES[0]?.[1] || 1;
  const STATE_RANK = useMemo(
    () => new Map(STATE_ENTRIES.map(([name], i) => [name, i + 1])),
    [STATE_ENTRIES],
  );
  const ACTIVE_STATES = STATE_ENTRIES.filter(([, v]) => v > 0).length;

  const REGION_TOTALS = useMemo(
    () =>
      REGION_ORDER.map((region) => ({
        region,
        value: STATE_ENTRIES.filter(([name]) => REGION_OF[name] === region).reduce(
          (s, [, v]) => s + v,
          0,
        ),
        color: REGION_COLORS[region],
      })).sort((a, b) => b.value - a.value),
    [STATE_ENTRIES],
  );

  const BEYOND_METRO_SHARE = TOTAL_USERS
    ? Math.round(
        (STATE_ENTRIES.filter(([n]) => !METRO_STATES.has(n)).reduce(
          (s, [, v]) => s + v,
          0,
        ) /
          TOTAL_USERS) *
          100,
      )
    : 0;

  // Merge real per-metro counts onto the static city coordinate list — the
  // lat/lng pins are real geography either way, only the counts change.
  const cities = useMemo(() => {
    if (!isLive) return CITIES;
    const realCounts = new Map(geography.metros.map((m) => [m.name, m.users]));
    return CITIES.filter((c) => realCounts.has(c.name)).map((c) => ({
      ...c,
      users: realCounts.get(c.name),
    }));
  }, [geography, isLive]);

  const topStates = useMemo(() => STATE_ENTRIES.slice(0, 7), [STATE_ENTRIES]);
  const topStateName = STATE_ENTRIES[0]?.[0] || "—";

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <div>
          <Text fw={800} size="xl">
            Geographic Reach — Where India Meets OTA
          </Text>
          <Text size="sm" c="dimmed" maw={560}>
            Registered fandom across every state and union territory. Darker =
            denser. Glowing pins mark the metros driving early traction.
          </Text>
        </div>
        <Badge
          size="lg"
          variant="light"
          leftSection={<IconMapPin size={13} />}
          color="grape"
        >
          {ACTIVE_STATES} States &amp; UTs Live
        </Badge>
      </Group>

      {/* KPI row */}
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
        <GeoKpi
          icon={<IconUsers size={15} />}
          color="indigo"
          label={isLive ? "Classified Users" : "Total Users"}
          value={fmt(TOTAL_USERS)}
          sub={
            isLive
              ? `${fmt(geography.unclassified)} more w/ unrecognized location`
              : "Demo data"
          }
        />
        <GeoKpi
          icon={<IconMap2 size={15} />}
          color="grape"
          label="States & UTs Reached"
          value={`${ACTIVE_STATES} / 36`}
          sub="Pan-India footprint"
        />
        <GeoKpi
          icon={<IconTrophy size={15} />}
          color="orange"
          label="#1 State"
          value={topStateName}
          sub={`${fmt(MAX_STATE)} users`}
        />
        <GeoKpi
          icon={<IconBuildingSkyscraper size={15} />}
          color="teal"
          label="Beyond Top-4 Metros"
          value={`${BEYOND_METRO_SHARE}%`}
          sub="Tier-2 & tier-3 momentum"
        />
      </SimpleGrid>

      <Grid gutter="lg">
        {/* ---- THE MAP ---- */}
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Card
            withBorder
            radius="lg"
            p="xl"
            h="100%"
            component={motion.div}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              background:
                "radial-gradient(120% 120% at 50% 0%, #faf8ff 0%, #ffffff 60%)",
            }}
          >
            <Group justify="space-between" mb="md">
              <Text fw={700}>User Density by State</Text>
              <Badge variant="dot" color={isLive ? "teal" : "gray"} size="sm">
                {isLive ? "Live data" : "Demo data"}
              </Badge>
            </Group>

            <Box
              style={{ position: "relative" }}
              onMouseMove={handleMove}
              onMouseLeave={() => setHovered(null)}
            >
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ center: [82.7, 22.9], scale: 1000 }}
                width={640}
                height={620}
                style={{ width: "100%", height: "auto" }}
              >
                <Geographies geography={INDIA_TOPO}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const name = geo.properties.st_nm;
                      const users = STATE_USERS_LIVE[name] || 0;
                      const isHot = hovered && hovered.name === name;
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          className="india-state-path"
                          onMouseEnter={() => setHovered({ name, users })}
                          fill={colorForState(users, MAX_STATE)}
                          stroke="#ffffff"
                          strokeWidth={0.6}
                          style={{
                            default: { outline: "none" },
                            hover: {
                              outline: "none",
                              fill: users ? "#5B21B6" : "#E9E3F7",
                              filter:
                                "drop-shadow(0 2px 6px rgba(91,33,182,0.35))",
                            },
                            pressed: { outline: "none" },
                          }}
                          opacity={hovered && !isHot ? 0.82 : 1}
                        />
                      );
                    })
                  }
                </Geographies>

                {cities.map((city) => {
                  const r = cityRadius(city.users);
                  return (
                    <Marker key={city.name} coordinates={city.coordinates}>
                      <circle
                        className="india-city-pulse"
                        r={r}
                        fill="#22D3EE"
                        fillOpacity={0.5}
                      />
                      <circle
                        r={r}
                        fill="#0891B2"
                        stroke="#ffffff"
                        strokeWidth={1.4}
                        fillOpacity={0.95}
                      />
                      {city.label && (
                        <text
                          textAnchor={city.label === "left" ? "end" : "start"}
                          x={city.label === "left" ? -(r + 4) : r + 4}
                          y={4}
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            fill: "#0E7490",
                            paintOrder: "stroke",
                            stroke: "#ffffff",
                            strokeWidth: 3,
                            strokeLinejoin: "round",
                          }}
                        >
                          {city.name}
                        </text>
                      )}
                    </Marker>
                  );
                })}
              </ComposableMap>

              {/* Floating tooltip */}
              {hovered && (
                <Box
                  style={{
                    position: "absolute",
                    left: pos.x + 14,
                    top: pos.y + 14,
                    pointerEvents: "none",
                    background: "rgba(17,24,39,0.94)",
                    color: "#fff",
                    padding: "8px 12px",
                    borderRadius: 10,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                    zIndex: 5,
                    minWidth: 150,
                  }}
                >
                  <Text size="sm" fw={700} c="white">
                    {hovered.name}
                  </Text>
                  {hovered.users ? (
                    <>
                      <Text size="xs" style={{ color: "#C4B5FD" }}>
                        {fmt(hovered.users)} users ·{" "}
                        {((hovered.users / TOTAL_USERS) * 100).toFixed(1)}%
                      </Text>
                      <Text size="xs" style={{ color: "rgba(255,255,255,0.6)" }}>
                        Rank #{STATE_RANK.get(hovered.name)} ·{" "}
                        {REGION_OF[hovered.name]} zone
                      </Text>
                    </>
                  ) : (
                    <Text size="xs" style={{ color: "rgba(255,255,255,0.6)" }}>
                      No users yet — whitespace
                    </Text>
                  )}
                </Box>
              )}
            </Box>

            {/* Legend */}
            <Group justify="space-between" mt="md" align="center">
              <Group gap={8} align="center">
                <span
                  style={{
                    display: "inline-block",
                    width: 10,
                    height: 10,
                    borderRadius: 3,
                    background: "#22D3EE",
                  }}
                />
                <Text size="xs" c="dimmed">
                  Metro hub (bubble size = users)
                </Text>
              </Group>
              <Group gap={8} align="center">
                <Text size="xs" c="dimmed">
                  Fewer
                </Text>
                <div
                  style={{
                    width: 160,
                    height: 10,
                    borderRadius: 6,
                    background: `linear-gradient(90deg, ${RAMP.join(",")})`,
                  }}
                />
                <Text size="xs" c="dimmed">
                  More users
                </Text>
              </Group>
            </Group>
          </Card>
        </Grid.Col>

        {/* ---- RIGHT COLUMN ---- */}
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Stack gap="lg" h="100%">
            {/* Top states leaderboard */}
            <Card withBorder radius="lg" p="xl">
              <Text fw={700} mb="xs">
                Top States
              </Text>
              <Text size="xs" c="dimmed" mb="lg">
                Ranked by registered users.
              </Text>
              <Stack gap="md">
                {topStates.map(([name, users], idx) => (
                  <Box key={name}>
                    <Group justify="space-between" mb={5}>
                      <Group gap={8}>
                        <Text
                          fw={800}
                          size="sm"
                          c={idx === 0 ? "grape.6" : "dimmed"}
                          w={22}
                        >
                          #{idx + 1}
                        </Text>
                        <Text fw={600} size="sm">
                          {name}
                        </Text>
                      </Group>
                      <Text fw={700} size="sm">
                        {fmt(users)}
                      </Text>
                    </Group>
                    <Progress
                      value={(users / MAX_STATE) * 100}
                      radius="xl"
                      size="md"
                      styles={{
                        section: {
                          background:
                            "linear-gradient(90deg,#07b9ce -14%,#3969e7 36%,#7d2ae7 106%)",
                        },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Card>

            {/* Zonal distribution */}
            <Card withBorder radius="lg" p="xl">
              <Text fw={700} mb="lg">
                Zonal Distribution
              </Text>
              <Group align="center" justify="center" gap="xl" wrap="nowrap">
                <ZonalDonut data={REGION_TOTALS} total={TOTAL_USERS} />
                <Stack gap={8} style={{ flex: 1 }}>
                  {REGION_TOTALS.map((d) => (
                    <Group key={d.region} justify="space-between" gap="xs">
                      <Group gap={8}>
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 3,
                            background: d.color,
                            display: "inline-block",
                          }}
                        />
                        <Text size="xs" fw={500}>
                          {d.region}
                        </Text>
                      </Group>
                      <Text size="xs" c="dimmed" fw={600}>
                        {Math.round((d.value / TOTAL_USERS) * 100)}%
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </Group>
            </Card>
          </Stack>
        </Grid.Col>
      </Grid>

      {/* ---- BOTTOM: Top metros + AI insight ---- */}
      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 7 }}>
          <Card withBorder radius="lg" p="xl" h="100%">
            <Text fw={700} mb="lg">
              Top Metros
            </Text>
            <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
              {cities.slice(0, 6).map((city) => (
                <Paper key={city.name} radius="md" p="md" withBorder>
                  <Group gap={6} mb={4}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 999,
                        background: "#0891B2",
                        display: "inline-block",
                      }}
                    />
                    <Text size="xs" c="dimmed" fw={600}>
                      {city.name}
                    </Text>
                  </Group>
                  <Text fw={800} size="lg">
                    {fmt(city.users)}
                  </Text>
                </Paper>
              ))}
            </SimpleGrid>
          </Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Card
            withBorder
            radius="lg"
            p="xl"
            h="100%"
            style={{
              background:
                "linear-gradient(135deg, rgba(125,42,231,0.06), rgba(7,185,206,0.06))",
            }}
          >
            <Group gap="xs" mb="xs">
              <ThemeIcon color="grape" variant="light" radius="xl">
                <IconSparkles size={16} />
              </ThemeIcon>
              <Text fw={700} size="sm">
                Expansion Signal
              </Text>
              <Badge color="grape" variant="light" size="xs" ml="auto">
                Geo Forecasting
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              Tier-2 hubs like <b>Jaipur</b>, <b>Kochi</b> and <b>Lucknow</b>{" "}
              are compounding at{" "}
              <Text span fw={700} c="grape.7">
                <IconArrowUpRight
                  size={13}
                  style={{ verticalAlign: "middle" }}
                />
                52% MoM
              </Text>{" "}
              — outpacing the metros. The model flags <b>Pune</b> and{" "}
              <b>Hyderabad</b> as the next cities ready for an on-ground OTA
              event activation.
            </Text>
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
